import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';
import React from 'react';
import { View, Pressable, StyleSheet, ViewStyle } from 'react-native';

import { DashboardSummaryCard, workOrderRowLabel } from '@/components/dashboards/dashboard-summary-card';
import { Text, useTheme } from '@/components/ui';
import { useFollowUps, useResolveFollowUp } from '@/hooks/use-data';
import type { FollowUp } from '@/types/models';
import { Alert } from '@/utils/alert';

// Matches the follow-up accent used on the work-order detail badge (#48).
const ACCENT = '#e65100';

/**
 * Pinned dashboard card surfacing the current user's work orders flagged as
 * needing follow-up (#48). Intentionally NOT gated by the tasks_enabled feature
 * flag — anyone with a follow-up sees it. Tapping a row opens the work order (for
 * photos, notes, description); "Done" resolves the follow-up.
 */
export const FollowUpsCard: React.FC<{ style?: ViewStyle }> = ({ style }) => {
	const theme = useTheme();
	const { data, isLoading } = useFollowUps();
	const resolve = useResolveFollowUp();

	const followUps = data ?? [];
	if (isLoading || followUps.length === 0) return null;

	const openWorkOrder = (f: FollowUp) => {
		router.push({ pathname: '/work-orders/[id]', params: { id: f.workOrderId } });
	};

	const confirmResolve = (f: FollowUp) => {
		Alert.alert(
			'Mark follow-up done?',
			`Resolve the follow-up for ${workOrderRowLabel(f)}. This clears the follow-up flag on the work order.`,
			[
				{ text: 'Cancel', style: 'cancel' },
				{ text: 'Mark done', onPress: () => resolve.mutate(f.taskId) },
			],
		);
	};

	return (
		<DashboardSummaryCard
			testID="follow-ups-card"
			icon="alert-circle"
			accent={ACCENT}
			title="Follow-ups Needed"
			count={followUps.length}
			style={style}
		>
			{followUps.map((f, i) => (
				<View key={f.taskId} style={[styles.row, { backgroundColor: i % 2 === 0 ? 'rgba(230,81,0,0.06)' : 'transparent' }]}>
					<Pressable
						testID={`follow-up-row-${f.taskId}`}
						onPress={() => openWorkOrder(f)}
						style={styles.rowMain}
					>
						<Text numberOfLines={1} style={[styles.rowTitle, { color: theme.colors.onSurface }]}>
							{workOrderRowLabel(f)}
						</Text>
						{f.followUpNote ? (
							<Text numberOfLines={2} style={[styles.rowNote, { color: theme.colors.onSurfaceVariant }]}>
								{f.followUpNote}
							</Text>
						) : null}
					</Pressable>
					<Pressable
						testID={`follow-up-resolve-${f.taskId}`}
						onPress={() => confirmResolve(f)}
						hitSlop={8}
						style={[styles.doneButton, { borderColor: ACCENT }]}
					>
						<Text style={{ fontSize: 12, fontWeight: '600', color: ACCENT }}>Done</Text>
					</Pressable>
					<Ionicons name="chevron-forward" size={16} color={theme.colors.onSurfaceVariant} />
				</View>
			))}
		</DashboardSummaryCard>
	);
};

const styles = StyleSheet.create({
	row: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 10,
		paddingVertical: 8,
		paddingHorizontal: 8,
	},
	rowMain: {
		flex: 1,
		minWidth: 0,
	},
	rowTitle: {
		fontSize: 14,
		fontWeight: '600',
	},
	rowNote: {
		fontSize: 13,
		marginTop: 2,
	},
	doneButton: {
		paddingVertical: 4,
		paddingHorizontal: 10,
		borderRadius: 8,
		borderWidth: 1,
	},
});
